import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import VersionHistoryItem from 'notebook/version-history/VersionHistoryItem';
import VersionHistoryItemPublishing from 'notebook/version-history/VersionHistoryItemPublishing';
import {VersionHistoryPanelContext} from 'notebook/version-history/VersionHistoryPanel';
import {shouldRenderRowAsPublishingItem} from 'notebook/version-history/VersionHistoryPanelUtil';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

function VersionHistoryItemRowDropdownCaret({
  shouldShow,
  isExpanded,
  onClick,
}: {
  shouldShow: boolean;
  isExpanded: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <div className={css(styles.rowWrapperCaret)} onClick={onClick}>
      {shouldShow &&
        (isExpanded ? <BenchlingIcon icon="caret-down" /> : <BenchlingIcon icon="caret-right" />)}
    </div>
  );
}

export default function VersionHistoryPanelRow({
  rowData,
  context,
}: {
  rowData: Version;
  context: VersionHistoryPanelContext;
}): JSX.Element {
  const {
    openVersionId,
    navigateAndSetOpenVersionId,
    resetToVersion,
    enableDuplicateFromVersion,
    duplicateFromVersion,
    hasPublishingVersions,
    isPublishingVersionExpanded,
    shouldShowPublishingVersionToggle,
    togglePublishingVersion,
    shouldEnableResetToVersionForRow,
  } = context;
  const isSelected = rowData.id === openVersionId;
  const onClick = (): void => {
    navigateAndSetOpenVersionId(rowData.id);
  };
  const resetToVersionOnClick: (() => void) | undefined = shouldEnableResetToVersionForRow(rowData)
    ? () => {
        resetToVersion(rowData);
      }
    : undefined;
  const duplicateFromVersionOnClick: (() => void) | undefined = enableDuplicateFromVersion
    ? () => {
        duplicateFromVersion(rowData);
      }
    : undefined;

  const renderAsPublishingItem = shouldRenderRowAsPublishingItem(rowData);
  return (
    <div
      className={css(styles.rowWrapper, isSelected && styles.rowWrapperIsSelected)}
      {...getTestProps({component: 'VersionHistoryPanelRow', element: 'root'})}
    >
      {hasPublishingVersions && (
        <VersionHistoryItemRowDropdownCaret
          shouldShow={shouldShowPublishingVersionToggle(rowData)}
          isExpanded={isPublishingVersionExpanded(rowData.id)}
          onClick={() => {
            togglePublishingVersion(rowData.id);
          }}
        />
      )}
      <div className={css(styles.rowWrapperContent)}>
        {rowData.publishableVersionInfo && renderAsPublishingItem ? (
          <VersionHistoryItemPublishing
            publishableVersionInfo={rowData.publishableVersionInfo}
            versionDatetime={rowData.modifiedAt}
            versionCreator={rowData.creator}
            isSelected={isSelected}
            isExpanded={isPublishingVersionExpanded(rowData.id)}
            onClick={onClick}
            createDraftOnClick={resetToVersionOnClick}
            duplicateFromVersionOnClick={duplicateFromVersionOnClick}
            oauth2ClientId={rowData.oauth2ClientId}
            oauth2ClientName={rowData.oauth2ClientName}
          />
        ) : (
          <VersionHistoryItem
            versionDatetime={rowData.modifiedAt}
            versionCreator={rowData.creator}
            isSelected={isSelected}
            onClick={onClick}
            resetToVersionOnClick={resetToVersionOnClick}
            duplicateFromVersionOnClick={duplicateFromVersionOnClick}
            oauth2ClientId={rowData.oauth2ClientId}
            oauth2ClientName={rowData.oauth2ClientName}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rowWrapper: {
    display: 'flex',
    flexDirection: 'row',
    ':hover': {
      backgroundColor: Color.GRAY_50,
    },
  },
  rowWrapperIsSelected: {
    backgroundColor: Color.BLUE_100,
    ':hover': {
      backgroundColor: Color.BLUE_100,
    },
  },
  rowWrapperCaret: {
    flex: '0 0',
    cursor: 'pointer',
    paddingTop: '10px',
    paddingLeft: '15px',
    minWidth: '25px',
    maxWidth: '25px',
  },
  rowWrapperContent: {
    flex: 1,
    minWidth: 0,
  },
});
